export interface MohitInvoiceJson {
  id?: string;
  status?: string;
  invoice_number?: string;
  purchase_order?: string;
  issued_date?: string;
  due_date?: string;
  amount_received?: number;
  payment_received_date?: string;
  client_invoice_number?: string;
}

export interface InfluencerInvoiceJson {
  id?: string;
  invoice_number?: string;
  received_date?: string;
  amount?: number;
  status?: string;
  payment_date?: string;
  // The shape of invoices read from the invoices table (IM, sponsorships)
  issue_date?: string;
  payment_received_date?: string;
  source_name?: string;
}

export interface InvoiceLine {
  side: 'client' | 'supplier';
  /** Who it's from, when it isn't the campaign's creator. A project's cost has only this. */
  label?: string;
  status_en: string;
  status_pt: string;
  category: PaymentCategory;
  invoice_number: string;
  client_invoice_number: string;
  purchase_order: string;
  issued_date: string;
  due_date: string;
  payment_date: string;
  amount: number;
  amount_paid: number;
  balance: number;
  days_to_payment: number | null;
  days_overdue: number;
}

export interface FinancialReportEntry {
  id: string;
  work_code: string;
  campaign_ref: string;
  campaign_mode: string;
  year: number;
  client: string;
  brand: string;
  campaign_status: string;
  agent: string;
  fee: number;
  commission_pct: number;
  commission_amount: number;
  influencers: string;
  influencer_count: number;
  campaign_budget: number;
  notes: string;
  created_at: string;
  updated_at: string;
  client_invoice_numbers: string;
  client_invoice_status: string;
  client_invoice_payment_dates: string;
  client_invoice_amount: number;
  influencer_invoice_numbers: string;
  influencer_invoice_status: string;
  influencer_invoice_payment_dates: string;
  influencer_invoice_amount: number;
}

export type PaymentCategory =
  | 'paid'
  | 'partial'
  | 'issued'
  | 'overdue'
  | 'notissued'
  | 'cancelled'
  | 'draft'
  | 'none';

export interface ProcessedEntry extends FinancialReportEntry {
  client_payment_category: PaymentCategory;
  influencer_payment_category: PaymentCategory;
  net_influencer_cost: number;
  net_agency_margin: number;

  client_invoices: InvoiceLine[];
  supplier_invoices: InvoiceLine[];

  client_billed: number;
  client_received: number;
  client_balance_due: number;
  supplier_billed: number;
  supplier_paid: number;
  supplier_balance_due: number;

  next_client_due_date: string | null;
  next_supplier_due_date: string | null;
  max_client_days_overdue: number;
  max_supplier_days_overdue: number;
  /** Projects: lines charged with no cost filled in, so the margin may be too high. */
  missing_cost_lines?: number;
}

/** Every kind of work the report covers, in the order it's filtered by. */
export const REPORT_MODE_LABELS: Record<string, string> = {
  brand_action: 'Ação Marca',
  influencer_marketing: 'Marketing de Influência',
  sponsorship: 'Patrocínio',
  services: 'Serviços',
  projecto: 'Projecto',
};

export interface FinancialSummary {
  total_fees: number;
  total_commissions: number;
  total_client_invoiced: number;
  total_influencer_invoiced: number;
  client_paid: number;
  client_issued: number;
  client_overdue: number;
  client_draft: number;
  client_unknown: number;
  influencer_paid: number;
  influencer_issued: number;
  influencer_overdue: number;
  influencer_unknown: number;
  client_balance_outstanding: number;
  supplier_balance_outstanding: number;
  pipeline_fees: number;
  pipeline_commissions: number;
  campaigns_completed: number;
  campaigns_billing: number;
  campaigns_in_progress: number;
  campaigns_consultation: number;
}

export interface AgentSummary {
  name: string;
  campaign_count: number;
  total_fees: number;
  total_commissions: number;
}

export interface InfluencerSummary {
  name: string;
  campaign_count: number;
  total_fees: number;
  total_client_invoiced: number;
  total_influencer_invoiced: number;
}

export type ReportTab = 'overview' | 'receivables' | 'payables' | 'pipeline' | 'agents';
export type SortField =
  | 'work_code'
  | 'brand'
  | 'fee'
  | 'client_invoice_amount'
  | 'influencer_invoice_amount'
  | 'campaign_status'
  | 'client_balance_due'
  | 'supplier_balance_due';
export type SortDirection = 'asc' | 'desc';
